import React, { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Icon from 'components/DesignSystem/Icon';
import CentralizedSpinner from 'components/CentralizedSpinner';
import { AgroOverviewLocation } from 'types/Meteorology/Agro/Overview/Dashboard';
import {
  LOCATION_COLUMNS,
  filterAndSortLocations,
} from 'helper/Meteorology/Agro/Overview/locationsTable';
import {
  selectAgroOverviewTableSearch,
  selectAgroOverviewTableSortBy,
  selectAgroOverviewTableSortDirection,
  setTableSearch,
} from 'redux/reducers/meteorologyAgroOverviewDashboardReducer';
import { cssVar } from 'constants/theme';
import LocationCard from './LocationCard';
import SortSheet from './SortSheet';

import './index.scss';

export default function LocationsList({
  locations,
  total,
  loading,
  onSelect,
}: {
  locations: AgroOverviewLocation[];
  total: number | undefined;
  loading: boolean;
  onSelect: (location: AgroOverviewLocation) => void;
}) {
  const dispatch = useDispatch();
  const search = useSelector(selectAgroOverviewTableSearch);
  const sortBy = useSelector(selectAgroOverviewTableSortBy);
  const sortDirection = useSelector(selectAgroOverviewTableSortDirection);

  const [sortOpen, setSortOpen] = useState(false);

  const rows = useMemo(
    () => filterAndSortLocations(locations, search, sortBy, sortDirection),
    [locations, search, sortBy, sortDirection],
  );

  const isEmptyState = total === 0;
  const count = total ?? rows.length;
  const sortColumn = LOCATION_COLUMNS.find((c) => c.key === sortBy);
  const sortLabel = sortColumn?.sortLabel ?? sortColumn?.label ?? 'Ordenar';

  return (
    <div className="agro-overview-mobile-locations">
      <div className="agro-overview-mobile-locations-header">
        <div className="agro-overview-mobile-locations-heading">
          <span className="agro-overview-mobile-locations-title">Localidades monitoradas</span>
          <span className="agro-overview-mobile-locations-count-badge">{count}</span>
        </div>
        <button
          type="button"
          className="agro-overview-mobile-locations-sort"
          onClick={() => setSortOpen(true)}
          aria-label="Ordenar localidades"
        >
          <Icon iconType="sort_ascending" color={cssVar('text-strong')} size="small" />
          <span className="agro-overview-mobile-locations-sort-label">{sortLabel}</span>
          <Icon iconType="down_arrow" color={cssVar('text-strong')} size="small" />
        </button>
      </div>

      <div className="agro-overview-mobile-locations-search">
        <Icon
          iconType="search"
          color={cssVar('text-muted')}
          size="small"
          className="agro-overview-mobile-locations-search-icon"
        />
        <Form.Control
          type="text"
          placeholder="Buscar localidade..."
          value={search}
          onChange={(event) => dispatch(setTableSearch(event.target.value))}
          className="agro-overview-mobile-locations-search-input"
        />
      </div>

      {loading && <CentralizedSpinner />}

      {!loading && isEmptyState && (
        <div className="agro-overview-mobile-locations-empty">
          <svg
            className="agro-overview-mobile-locations-empty-icon"
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 21s-7-5.686-7-11a7 7 0 0 1 14 0c0 5.314-7 11-7 11Z" />
            <circle cx="12" cy="10" r="2.5" />
          </svg>
          <p className="agro-overview-mobile-locations-empty-title">Nenhum local cadastrado</p>
          <p className="agro-overview-mobile-locations-empty-subtitle">
            Cadastre um local para monitorar as condições hídricas.
          </p>
          <Link to="/agro/register" className="agro-overview-mobile-locations-empty-cta">
            <Icon iconType="add" color={cssVar('text-on-accent')} size="small" />
            Cadastrar local
          </Link>
        </div>
      )}

      {!loading && !isEmptyState && (
        rows.length === 0 ? (
          <div className="agro-overview-mobile-locations-noresults">
            Nenhuma localidade encontrada.
          </div>
        ) : (
          <div className="agro-overview-mobile-locations-cards">
            {rows.map((location) => (
              <LocationCard
                key={location.id}
                location={location}
                onClick={() => onSelect(location)}
              />
            ))}
          </div>
        )
      )}

      <SortSheet open={sortOpen} dismiss={() => setSortOpen(false)} />
    </div>
  );
}
